import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import usePageMeta from "@/hooks/usePageMeta";
import { getNewsById, newsList, newsTypeLabel } from "@/mocks/news";
import { getWorkById } from "@/mocks/works";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = getNewsById(id ?? "");

  usePageMeta({
    title: item ? `翔田千里动态资讯－${item.title}` : "资讯未找到－翔田千里动态资讯",
    description: item
      ? `${item.summary.slice(0, 80)} 发布日期：${item.date}，分类：${newsTypeLabel(item.type)}。`
      : "该资讯页面不存在，可返回动态资讯列表查看全部内容。",
    keywords: item
      ? `${item.tags.join(",")},翔田千里动态,资讯详情`
      : "翔田千里动态资讯",
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="container-page flex min-h-[60vh] flex-col items-center justify-center py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-error-warning-line" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl text-foreground-950">没有找到这条资讯</h1>
          <p className="mt-2 max-w-sm text-sm text-foreground-600">链接可能已失效，或者该资讯尚未收录。</p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回动态列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const relatedWork = item.relatedWorkId ? getWorkById(item.relatedWorkId) : undefined;
  const related = newsList
    .filter((news) => news.id !== item.id && news.tags.some((tag) => item.tags.includes(tag)))
    .slice(0, 3);
  const fallbackRelated = newsList.filter((news) => news.id !== item.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-primary-950">
        <img
          src={item.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-25 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/60"></div>
        <div className="container-page relative py-9 md:py-14">
          <Breadcrumb
            tone="dark"
            items={[{ label: "首页", to: "/" }, { label: "动态资讯", to: "/news" }, { label: "详情" }]}
          />
          <div className="mt-6 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="rounded-md bg-accent-500 px-2.5 py-1 font-medium text-primary-950">
                {newsTypeLabel(item.type)}
              </span>
              <span className="rounded-md border border-background-50/25 px-2.5 py-1 text-background-50/75">
                {item.date}
              </span>
              <span className="text-background-50/50">翔田千里资讯</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl leading-snug text-background-50 md:text-[38px] md:leading-tight">
              {item.title}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-background-50/70">{item.summary}</p>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-8 md:py-12">
        <div className="container-page grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px] lg:gap-8">
          <article className="overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
            <div className="relative aspect-[16/9] w-full bg-primary-900">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} 翔田千里动态资讯`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="p-5 md:p-8">
              <div className="space-y-6">
                {item.content.map((block, index) => (
                  <div key={`${item.id}-block-${index}`}>
                    {block.heading && (
                      <h2 className="mb-3 font-heading text-xl text-foreground-950">{block.heading}</h2>
                    )}
                    <div className="space-y-3">
                      {block.paragraphs.map((paragraph, paragraphIndex) => (
                        <p
                          key={`${item.id}-p-${index}-${paragraphIndex}`}
                          className="text-sm leading-loose text-foreground-700"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    {block.image && (
                      <figure className="mt-5">
                        <div className="aspect-[16/10] w-full overflow-hidden rounded-md bg-primary-900">
                          <img
                            src={block.image}
                            alt={block.caption || item.title}
                            title={`${item.title} 配图`}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                        {block.caption && (
                          <figcaption className="mt-2 text-[11px] text-foreground-400">{block.caption}</figcaption>
                        )}
                      </figure>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200/70 pt-5">
                {item.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-50 px-3 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-100"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 rounded-md border border-background-300/80 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true"></i>
                  返回动态列表
                </Link>
                {relatedWork && (
                  <Link
                    to={`/works/detail/${relatedWork.id}`}
                    className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-film-line" aria-hidden="true"></i>
                    相关作品《{relatedWork.title}》
                  </Link>
                )}
              </div>
            </div>
          </article>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-[11px] uppercase tracking-wider2 text-foreground-400">资讯信息</h2>
              <dl className="mt-3 space-y-3 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-400">发布日期</dt>
                  <dd className="text-foreground-900">{item.date}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-400">资讯类型</dt>
                  <dd className="text-foreground-900">{newsTypeLabel(item.type)}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-400">内容标签</dt>
                  <dd className="text-foreground-900">{item.tags.length} 个</dd>
                </div>
              </dl>
            </div>

            {relatedWork && (
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <h2 className="text-[11px] uppercase tracking-wider2 text-foreground-400">提及作品</h2>
                <Link to={`/works/detail/${relatedWork.id}`} className="group mt-3 flex gap-3">
                  <div className="h-24 w-16 shrink-0 overflow-hidden rounded bg-primary-900">
                    <img
                      src={relatedWork.cover}
                      alt={relatedWork.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div>
                    <p className="font-heading text-sm text-foreground-950 group-hover:text-primary-700">
                      {relatedWork.title}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-500">
                      {relatedWork.year} · {relatedWork.roleType}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-400">饰 {relatedWork.role}</p>
                  </div>
                </Link>
              </div>
            )}

            <div className="rounded-lg bg-primary-950 p-5 text-background-50">
              <h2 className="font-heading text-base">订阅式阅读</h2>
              <p className="mt-2 text-xs leading-relaxed text-background-50/70">
                本站不收集个人信息，所有资讯公开浏览。可通过浏览器收藏本页，随时回来查看更新。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-2 text-xs text-accent-300 hover:text-accent-200"
              >
                了解站点说明
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-page">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-px w-7 bg-accent-500"></span>
                <span className="text-[11px] uppercase tracking-wider2 text-accent-600">More News</span>
              </div>
              <h2 className="mt-3 font-heading text-2xl text-foreground-950">相关资讯推荐</h2>
              <p className="mt-2 text-xs text-foreground-500">依据资讯标签与作品分类匹配。</p>
            </div>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 rounded-md border border-background-300/80 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              全部资讯
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {relatedList.map((news) => (
              <NewsCard key={news.id} item={news} />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}